// Helpers partagés (calculs de stats projet, formatage dates, etc.)

import type { Project, ProjectStats, Criterion } from './types';

// ── Palette critères (mode "Tout") ───────────────────────────────────
// Teintes Tailwind écrites en dur : Tailwind JIT ne détecte pas les
// noms dynamiques type `bg-${color}-100`, il faut que les classes soient
// littérales dans la source. On évite volontairement emerald / amber /
// violet : ces trois couleurs encodent déjà le statut d'une citation
// (vérifiée / IA en attente / manuelle) dans le reste de l'app.

export interface CriterionColor {
  key: string;
  bg: string; // fond surlignage
  text: string; // texte sur fond surlignage
  border: string; // bordure panneau / chip
  softBg: string; // fond panneau léger
  dot: string; // pastille 500 (légende, pastille chip)
}

export const CRITERION_PALETTE: CriterionColor[] = [
  { key: 'rose',    bg: 'bg-rose-100',    text: 'text-rose-900',    border: 'border-rose-300',    softBg: 'bg-rose-50',    dot: 'bg-rose-500' },
  { key: 'orange',  bg: 'bg-orange-100',  text: 'text-orange-900',  border: 'border-orange-300',  softBg: 'bg-orange-50',  dot: 'bg-orange-500' },
  { key: 'yellow',  bg: 'bg-yellow-100',  text: 'text-yellow-900',  border: 'border-yellow-300',  softBg: 'bg-yellow-50',  dot: 'bg-yellow-500' },
  { key: 'lime',    bg: 'bg-lime-100',    text: 'text-lime-900',    border: 'border-lime-300',    softBg: 'bg-lime-50',    dot: 'bg-lime-500' },
  { key: 'teal',    bg: 'bg-teal-100',    text: 'text-teal-900',    border: 'border-teal-300',    softBg: 'bg-teal-50',    dot: 'bg-teal-500' },
  { key: 'cyan',    bg: 'bg-cyan-100',    text: 'text-cyan-900',    border: 'border-cyan-300',    softBg: 'bg-cyan-50',    dot: 'bg-cyan-500' },
  { key: 'sky',     bg: 'bg-sky-100',     text: 'text-sky-900',     border: 'border-sky-300',     softBg: 'bg-sky-50',     dot: 'bg-sky-500' },
  { key: 'blue',    bg: 'bg-blue-100',    text: 'text-blue-900',    border: 'border-blue-300',    softBg: 'bg-blue-50',    dot: 'bg-blue-500' },
  { key: 'indigo',  bg: 'bg-indigo-100',  text: 'text-indigo-900',  border: 'border-indigo-300',  softBg: 'bg-indigo-50',  dot: 'bg-indigo-500' },
  { key: 'fuchsia', bg: 'bg-fuchsia-100', text: 'text-fuchsia-900', border: 'border-fuchsia-300', softBg: 'bg-fuchsia-50', dot: 'bg-fuchsia-500' },
  { key: 'pink',    bg: 'bg-pink-100',    text: 'text-pink-900',    border: 'border-pink-300',    softBg: 'bg-pink-50',    dot: 'bg-pink-500' },
];

/**
 * Associe une couleur stable à chaque critère feuille actif, dans l'ordre
 * croissant du code (1.1 → rose, 1.2 → orange, …). Au-delà de 11 critères
 * on boucle — acceptable pour le prototype, les doublons restent rares en
 * pratique (les grilles tiennent généralement < 15 critères).
 */
export function buildCriterionColorMap(
  activeLeafCriteria: Criterion[]
): Map<string, CriterionColor> {
  const map = new Map<string, CriterionColor>();
  activeLeafCriteria.forEach((c, i) => {
    map.set(c.id, CRITERION_PALETTE[i % CRITERION_PALETTE.length]);
  });
  return map;
}

/**
 * Calcule les stats d'un projet pour affichage dans les cards du dashboard.
 * Les "évaluations attendues" = nb de candidats × nb de critères feuilles actifs.
 * Une évaluation est "faite" dès qu'elle a une note (IA ou user).
 */
export function computeStats(project: Project): ProjectStats {
  const regulationFiles = project.files.filter(f => f.source === 'regulation');
  const hasRegulation = regulationFiles.length > 0;

  const leafIds = getLeafCriteriaIds(project.criteria);
  const activeLeafIds = leafIds.filter(id => {
    const c = project.criteria.find(k => k.id === id);
    return c?.active;
  });

  const evaluationsExpected = activeLeafIds.length * project.candidates.length;
  const evaluationsDone = project.evaluations.filter(
    e =>
      activeLeafIds.includes(e.criterionId) &&
      (e.userScore !== undefined || e.aiScore !== undefined)
  ).length;

  return {
    hasRegulation,
    regulationFileCount: regulationFiles.length,
    candidateCount: project.candidates.length,
    criteriaCount: project.criteria.length,
    activeLeafCriteriaCount: activeLeafIds.length,
    evaluationsDone,
    evaluationsExpected,
    progressPct:
      evaluationsExpected === 0
        ? 0
        : Math.round((evaluationsDone / evaluationsExpected) * 100),
  };
}

/** Retourne les IDs des critères feuilles (= sans enfant direct). */
export function getLeafCriteriaIds(criteria: Criterion[]): string[] {
  const parents = new Set(criteria.map(c => c.parentId).filter(Boolean) as string[]);
  return criteria.filter(c => !parents.has(c.id)).map(c => c.id);
}

// ── Formatage dates (FR, référence : le "now" des mocks) ─────────────

const FR_DATE = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short' });
const FR_DATETIME = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

export function formatDate(iso: string): string {
  return FR_DATE.format(new Date(iso));
}

export function formatDateTime(iso: string): string {
  return FR_DATETIME.format(new Date(iso));
}

/**
 * Formatage relatif court : "il y a 3 min", "il y a 2 h", "il y a 4 j", puis date.
 * Prend un `referenceNow` en option pour que les dates mock soient stables
 * quelle que soit la date réelle d'ouverture de l'app (sinon "il y a 5 mois" etc.).
 */
export function formatRelative(iso: string, referenceNow?: string): string {
  const base = referenceNow ? new Date(referenceNow).getTime() : Date.now();
  const diff = base - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'à l\'instant';
  if (mins < 60) return `il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `il y a ${days} j`;
  return formatDate(iso);
}

// ── Formatage tailles fichier ────────────────────────────────────────

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}
